import {
  MutationrequestEngagementGoogleDocsReportArgs,
  MutationrequestPackageProposalExcelArgs,
  MutationrequestPackageProposalPptxArgs,
  MutationrequestProposalPptxArgs,
  MutationrequestProposalSpreadsheetArgs,
  MutationrequestTrendingInfluencersSpreadsheetArgs,
  MutationrequestUserProposalExcelArgs,
  MutationrequestUserProposalPptxArgs,
  useAuthGoogleMutation,
  useRequestEngagementGoogleDocsReportMutation,
  useRequestPackageProposalExcelMutation,
  useRequestPackageProposalPptxMutation,
  useRequestProposalPptxMutation,
  useRequestProposalSpreadsheetMutation,
  useRequestTrendingInfluencersSpreadsheetMutation,
  useRequestUserProposalExcelMutation,
  useRequestUserProposalPptxMutation
} from '@anytag/graphql';
import { manageLocalStorage } from '@anytag/router';
import { Loading } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';
import { useEffect } from 'react';
import { GoogleAuthDownloadJsonType } from '@anytag/shared-molecules';
import { DownloadFileType } from '@anytag/shared-types';

export const RedirectGoogleAuth = () => {
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { callAuthGoogle } = useAuthGoogleMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestProposalSpreadsheet } = useRequestProposalSpreadsheetMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestProposalPptx } = useRequestProposalPptxMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestUserProposalPptx } = useRequestUserProposalPptxMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestUserProposalExcel } = useRequestUserProposalExcelMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestPackageProposalPptx } = useRequestPackageProposalPptxMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestPackageProposalExcel } = useRequestPackageProposalExcelMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestEngagementGoogleDocsReport } = useRequestEngagementGoogleDocsReportMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestTrendingInfluencersSpreadsheet } = useRequestTrendingInfluencersSpreadsheetMutation({
    fetchPolicy: 'no-cache'
  });

  const savedDownloadJson = manageLocalStorage('get_json', 'google-auth-download-json') as GoogleAuthDownloadJsonType;
  const isInfluencersProposal = savedDownloadJson?.goBackUrl === '/influencers/proposal';
  const isEngagementProposal = savedDownloadJson?.goBackUrl === '/engagement/$id/proposal';
  const isEngagementReport = savedDownloadJson?.goBackUrl === '/engagement/$id/report';
  const isTrendingInfluencers = savedDownloadJson?.goBackUrl === '/trending/influencers';

  const handleAuthGoogle = async () => {
    try {
      const { data } = await callAuthGoogle({
        variables: {
          input: {
            response: window.location.search.slice(1),
            callbackUrl: window.location.origin + window.location.pathname
          }
        }
      });
      if (data?.authGoogle?.ok) {
        enqueueSnackbar(t('Successfully authenticated with Google'), { variant: 'success' });

        if (savedDownloadJson) {
          try {
            const { variables, format } = savedDownloadJson;
            if (format === DownloadFileType.GOOGLE_SLIDE) {
              if (isInfluencersProposal) {
                callRequestUserProposalPptx({
                  variables: variables as MutationrequestUserProposalPptxArgs
                });
              } else if (isEngagementProposal) {
                if ('engagementId' in variables && variables?.engagementId === undefined) return;
                callRequestProposalPptx({ variables: variables as MutationrequestProposalPptxArgs });
              } else {
                if ('packageId' in variables && !variables?.packageId) return;

                callRequestPackageProposalPptx({ variables: variables as MutationrequestPackageProposalPptxArgs });
              }
              enqueueSnackbar(t('Google Slide Report has been sent to your email and saved to the Google Drive'), {
                variant: 'success'
              });
            } else if (format === DownloadFileType.SPREADSHEET) {
              if (isInfluencersProposal) {
                callRequestUserProposalExcel({
                  variables: variables as MutationrequestUserProposalExcelArgs
                });
              } else if (isEngagementProposal) {
                if ('engagementId' in variables && variables?.engagementId === undefined) return;
                callRequestProposalSpreadsheet({
                  variables: variables as MutationrequestProposalSpreadsheetArgs
                });
              } else if (isTrendingInfluencers) {
                callRequestTrendingInfluencersSpreadsheet({
                  variables: variables as MutationrequestTrendingInfluencersSpreadsheetArgs
                });
              } else {
                if ('packageId' in variables && !variables?.packageId) return;
                callRequestPackageProposalExcel({
                  variables: variables as MutationrequestPackageProposalExcelArgs
                });
              }
              enqueueSnackbar(t('Google Sheets Report has been sent to your email and saved to the Google Drive'), {
                variant: 'success'
              });
            } else if (isEngagementReport) {
              if ('engagementId' in variables && variables?.engagementId === undefined) return;
              callRequestEngagementGoogleDocsReport({
                variables: variables as MutationrequestEngagementGoogleDocsReportArgs
              });
              enqueueSnackbar(t('Google Docs Report has been sent to your email and saved to the Google Drive'), {
                variant: 'success'
              });
            }
          } catch (error) {
            enqueueSnackbar(t('Failed to download proposal'), { variant: 'error' });
          }
        }
      }
    } catch (error) {
      enqueueSnackbar(t('Failed to authenticate, please try again later'), { variant: 'error' });
    } finally {
      navigate({
        to: savedDownloadJson?.goBackUrl,
        search: savedDownloadJson?.searchParams,
        params: { id: savedDownloadJson?.urlParams.engagementId || savedDownloadJson?.urlParams.packageId }
      });
      manageLocalStorage('remove', 'google-auth-download-json');
    }
  };

  useEffect(() => {
    handleAuthGoogle();
  }, []);

  return <Loading />;
};
